/**
 * TODO: Clean once new login policy is stabilized
 */
import axios, {
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseHeaders,
  Canceler,
  CancelToken
} from 'axios';
import auth from '@/services/auth';
import _url from '@/helpers/urlHelper';
import _log from '@/helpers/log';
import { ObjectOnly } from '@/classes/ObjectOnly';
// import { UserCredentials } from '@/classes/UserCredentials';

export default () => {
  return axios.create({
    baseURL: process.env.VITE_HYSIZE_BACK_END_BASE_URL
  });
};

// export an axiosWrapper service
const baseUrl = process.env.VITE_HYSIZE_BACK_END_BASE_URL;
const CancelToken = axios.CancelToken;

const cancellationTokens: any = {};

// intercept http errors to renew token
function errorResponseHandler(error: any) {
  const err = error instanceof Error ? error : new Error(String(error));

  if (axios.isCancel(error)) {
    return Promise.reject(err);
  }
  // check for errorHandle config
  if (Object.prototype.hasOwnProperty.call(error.config, 'errorHandle') && error.config.errorHandle === false) {
    return Promise.reject(err);
  }
  // if has response show the error
  if (error.response) {
    _log.logError(error);
    return Promise.reject(err);
  }
  return Promise.reject(err);
}

// apply interceptor on response
axios.interceptors.response.use((response) => response, errorResponseHandler);

export interface MyRequestConfig extends AxiosRequestConfig {
  numberAttempts?: number;
}

// export an axiosWrapper service
export const axiosWrapper = {
  // credentials: new UserCredentials(),

  request<T>(url: string, config: MyRequestConfig = {}): AxiosPromise<T> {
    // limit the reattemps number requests
    if (!config.numberAttempts) {
      config.numberAttempts = 0;
    }
    config.numberAttempts++;

    return auth.getToken().then((token) => {
      if (!token) {
        return Promise.reject(new Error(`No access token found when calling ${url}`));
      }
      axios.defaults.headers.common = {
        Authorization: 'bearer ' + token
      };
      // set default config https://github.com/axios/axios#handling-errors
      const requestConfig: AxiosRequestConfig = {
        // basic auth
        // auth: this.credentials,

        // `url` is the server URL that will be used for the request
        url,

        // `method` is the request method to be used when making the request
        method: 'get', // default

        // `baseURL` will be prepended to `url` unless `url` is absolute.
        // It can be convenient to set `baseURL` for an instance of axios to pass relative URLs
        // to methods of that instance.
        baseURL: baseUrl,

        // `headers` are custom headers to be sent
        headers: {},

        // `params` are the URL parameters to be sent with the request
        // Must be a plain object or a URLSearchParams object
        params: {},

        // `data` is the data to be sent as the request body
        // Only applicable for request methods 'PUT', 'POST', and 'PATCH'
        // When no `transformRequest` is set, must be of one of the following types:
        // - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
        // - Browser only: FormData, File, Blob
        // - Node only: Stream, Buffer
        data: {},

        // `withCredentials` indicates whether or not cross-site Access-Control requests
        // should be made using credentials
        withCredentials: false, // default false

        // `responseType` indicates the type of data that the server will respond with
        // options are 'arraybuffer', 'blob', 'document', 'json', 'text', 'stream'
        responseType: 'json' // default

        // `responseEncoding` indicates encoding to use for decoding responses
        // Note: Ignored for `responseType` of 'stream' or client-side requests
        // responseEncoding: 'utf8', // default

        // `transformRequest` allows changes to the request data before it is sent to the server
        // This is only applicable for request methods 'PUT', 'POST', and 'PATCH'
        // The last function in the array must return a string or an instance of Buffer, ArrayBuffer,
        // FormData or Stream
        // You may modify the headers object.
        // transformRequest: [
        // function (data, headers) {
        //   // Do whatever you want to transform the data
        //   return typeof data === 'string' ? data : data.stringify(data);
        // }],
      };
      // extend initConfig from config param
      Object.assign(requestConfig, config);
      return axios.request<T>(requestConfig);
    });
  },

  get<T>(url: string, config: MyRequestConfig): AxiosPromise<T> {
    return this.request(url, config);
  },

  post<T>(url: string, config: MyRequestConfig): AxiosPromise<T> {
    return this.request(url, {
      method: 'post',
      ...config
    });
  },

  put<T>(url: string, config: MyRequestConfig): AxiosPromise<T> {
    return this.request(url, {
      method: 'put',
      ...config
    });
  },

  delete<T>(url: string, config: MyRequestConfig): AxiosPromise<T> {
    return this.request(url, {
      method: 'delete',
      ...config
    });
  },

  async getWithConfigFromApi<T>(
    route: string,
    config?: ObjectOnly,
    cancellationTokenKey?: string
  ): Promise<{
    data: T;
    headers: AxiosResponseHeaders;
  }> {
    if (cancellationTokenKey) {
      axiosWrapper.cancelToken(cancellationTokenKey);
    }

    const response: AxiosResponse<T> = await axiosWrapper.get<T>(baseUrl + route, {
      cancelToken: axiosWrapper.getCancelToken(cancellationTokenKey),
      ...config,
      paramsSerializer: _url.getUrlParamsString
    });

    return {
      data: response.data,
      headers: response.headers as AxiosResponseHeaders
    };
  },

  getFromApi<T>(route: any, params?: any, cancellationTokenKey?: string): AxiosPromise<T> {
    if (cancellationTokenKey) {
      axiosWrapper.cancelToken(cancellationTokenKey);
    }
    const promise = axiosWrapper.get<T>(baseUrl + route, {
      cancelToken: axiosWrapper.getCancelToken(cancellationTokenKey),
      params,
      // I use my own function in order to avoid 'brackets' array not supported by ASP.NET
      paramsSerializer: _url.getUrlParamsString
    });
    return promise;
  },

  postToApi<T>(route: string, data?: any, cancellationTokenKey?: string): AxiosPromise<T> {
    if (cancellationTokenKey) {
      axiosWrapper.cancelToken(cancellationTokenKey);
    }
    const promise = axiosWrapper.post<T>(baseUrl + route, {
      cancelToken: axiosWrapper.getCancelToken(cancellationTokenKey),
      data
    });
    return promise;
  },

  putOnApi<T>(route: string, data?: any, cancellationTokenKey?: string): AxiosPromise<T> {
    if (cancellationTokenKey) {
      axiosWrapper.cancelToken(cancellationTokenKey);
    }
    const promise = axiosWrapper.put<T>(baseUrl + route, {
      cancelToken: axiosWrapper.getCancelToken(cancellationTokenKey),
      data
    });
    return promise;
  },

  deleteFromApi<T>(route: string, data?: any, cancellationTokenKey?: string): AxiosPromise<T> {
    if (cancellationTokenKey) {
      axiosWrapper.cancelToken(cancellationTokenKey);
    }
    const promise = axiosWrapper.delete<T>(baseUrl + route, {
      cancelToken: axiosWrapper.getCancelToken(cancellationTokenKey),
      data
    });
    return promise;
  },

  getFromLocalJson<T>(fileName: string): AxiosPromise<T> {
    // Cache-bust static locale/config JSON so a new deploy serves fresh files without a manual hard refresh.
    const buildId = process.env.VITE_BUILD_ID ?? '';
    const promise = axios.get<T>(`json/${fileName}.json?v=${buildId}`, {
      baseURL: window.location.origin
    });
    return promise;
  },

  cancelToken(tokenKey: string) {
    return cancellationTokens[tokenKey] && cancellationTokens[tokenKey](tokenKey);
  },

  getCancelToken(tokenKey?: string): CancelToken {
    return new CancelToken(function executor(c: Canceler) {
      // An executor function receives a cancel function as a parameter
      if (tokenKey) {
        cancellationTokens[tokenKey] = c;
      }
    });
  }

  // setCredentials(creds: UserCredentials) {
  //   if (creds instanceof UserCredentials) {
  //     this.credentials = creds;
  //   }
  // }
};
